import {
  progressReport200HoursType,
  progressReport400HoursType,
  endB1RegularType,
  scaleDownA2Type,
  projectReportItemLabels,
} from "./projects"

// The report variants the registry currently has field definitions for --
// a subset of projectReportCategoryItems.amsterdam_b1_route. Imports the
// exact backend report-type constants from projects.ts instead of
// duplicating the short identifiers.
export const reportVariant = [
  progressReport200HoursType,
  progressReport400HoursType,
  endB1RegularType,
  scaleDownA2Type,
] as const

export type ReportVariant = (typeof reportVariant)[number]

// Reuses the labels already defined in projectReportCategoryItems so a
// report's display label only needs to be maintained in one place.
export const reportVariantLabels: Record<ReportVariant, string> =
  Object.fromEntries(
    reportVariant.map((variant) => [variant, projectReportItemLabels[variant]]),
  ) as Record<ReportVariant, string>

export function isKnownReportVariant(
  value: string | undefined,
): value is ReportVariant {
  return reportVariant.includes(value as ReportVariant)
}

/**
 * A finalized report document, generated from
 * GET /students/:id/reports (StudentProgressReportController.index).
 */
export interface GeneratedStudentReport {
  id: number
  filename: string
  uploadType: string
  insertedAt: string
}

export interface GeneratedStudentReportsResponse {
  data: GeneratedStudentReport[]
}

/**
 * A report still awaiting manual input from the current toptaal user,
 * from GET /students/:id/reports/manual-input
 * (StudentProgressReportController.manual_input).
 */
export interface ManualInputStudentReport {
  id: number
  reportType: string
  reportLabel: string
  dueDate: string
  studentProjectOrganizationId: number
  project: {
    id: number
    name: string
  }
}

export interface ManualInputStudentReportsResponse {
  data: {
    needsManualInput: boolean
    reports: ManualInputStudentReport[]
  }
}

/**
 * Matches the real backend response from
 * PUT /api/toptaal/student_progress_reports/:id/submission (apiClient
 * camelCases this automatically -- see createApiClient's afterResponse
 * hook). No `variant`/`studentId` here -- the caller already knows both
 * (variant from the todo metadata, studentId from the route) since there's
 * no GET endpoint yet to fetch a report instance by id.
 */
export interface StudentProgressReportSubmission {
  id: number
  studentProgressReportId: number
  submittedByToptaalUserId: number
  fields: Record<string, unknown>
  insertedAt: string
  updatedAt: string
}

export interface StudentProgressReportSubmissionResponse {
  data: StudentProgressReportSubmission
}

export interface RoleSubmission {
  id: number
  studentProgressReportId: number
  submittedByToptaalUserId: number
  inputRole: "teacher" | "planner"
  fields: Record<string, unknown>
  insertedAt: string
  updatedAt: string
}

export interface StudentProgressReportRoleSubmissionsResponse {
  data: {
    teacher: RoleSubmission | null
    planner: RoleSubmission | null
  }
}

export interface ManualReportTodoCreationParams {
  studentProjectOrganizationId: number
  reportType: ReportVariant
  toptaalUserId: number
}

export interface ManualReportTodoCreationResponse {
  data: {
    studentProgressReportId: number
    todoId: number
    todoAssignmentId: number
    studentProjectOrganizationId: number
    projectId: number
    toptaalUserId: number
    reportType: ReportVariant
    status: "manual_input_required"
  }
}
